interface CashFlowsConfig {
  cashFlows?: string
}

export class CashFlowsTool {
  private api: any
  private config: CashFlowsConfig
  private block: any
  private isProcessing: boolean = false
  private headerInserted: boolean = false

  static get toolbox() {
    return {
      title: "Cash flows",
      icon: '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M10 2l2.1 4.26L17 7l-3.4 3.32L14.2 15 10 12.9 5.8 15l.6-4.68L3 7l4.9-.74L10 2z" fill="currentColor"/></svg>',
    }
  }

  constructor({
    api,
    config,
    block,
    data,
  }: {
    api: any
    config: CashFlowsConfig
    block: any
    data?: { header_inserted?: boolean }
  }) {
    this.api = api
    this.config = config || {}
    this.block = block
    this.headerInserted = data?.header_inserted || false
  }

  render() {
    if (this.api.readOnly?.isEnabled) {
      const p = document.createElement("p")
      p.className = "ej-cash-flows-content"
      p.textContent =
        this.config.cashFlows ||
        "No significant cash flow activity this quarter"
      return p
    }

    const root = document.createElement("div")
    root.className = "ej-cash-flows-inserter"

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    setTimeout(() => {
      if (!this.isProcessing) {
        this.insertParagraphAndReplace()
      }
    }, 0)

    return root
  }

  private async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Cash Flows" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  private insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current block
      this.api.blocks.insert(
        "header",
        {
          text: "Cash Flows",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the content block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  private async insertParagraphAndReplace() {
    if (this.isProcessing) return
    this.isProcessing = true

    try {
      const cashFlows =
        this.config.cashFlows ||
        "No significant cash flow activity this quarter"

      // Find our current position
      const currentIndex = this.findCurrentBlockIndex()
      if (currentIndex === -1) {
        console.warn("Could not find current block index")
        return
      }

      // Insert the paragraph block
      await this.api.blocks.insert(
        "paragraph",
        { text: cashFlows },
        {},
        currentIndex,
        false,
      )

      // Small delay to ensure the block is fully inserted.
      // Re-find the inserter block by ID because concurrent tool insertions
      // on the same page shift indices between insertion and cleanup.
      setTimeout(() => {
        try {
          const inserterIndex = this.findCurrentBlockIndex()
          if (inserterIndex !== -1) {
            this.api.blocks.delete(inserterIndex)
            this.api.caret.setToBlock(inserterIndex - 1)
          }
        } catch (error) {
          console.error("Error cleaning up inserter block:", error)
        }
      }, 50)
    } catch (error) {
      console.error("Error inserting cash flows paragraph:", error)
      this.isProcessing = false
    }
  }

  private findCurrentBlockIndex(): number {
    const totalBlocks = this.api.blocks.getBlocksCount()

    for (let i = 0; i < totalBlocks; i++) {
      try {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          return i
        }
      } catch (error) {
        continue
      }
    }

    return -1
  }

  save() {
    return {
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}

// Factory function to create the tool with specific config
export const createCashFlowsTool = (config: CashFlowsConfig) => {
  return class extends CashFlowsTool {
    constructor(toolConfig: any) {
      super({
        ...toolConfig,
        config: { ...config, ...toolConfig.config },
      })
    }
  }
}
